import { useEffect, useRef } from 'react';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PartUploadField,
  usePartUpload,
  usePartAnalysis,
} from '@/components/parts';
import { useHomepage } from '@/contexts/homepage-context';
import {
  IMPLICIT_PRIMITIVE_LABELS,
  LatticeFillParams,
  NASA_LATTICE_TOPOLOGY_ORDER,
} from '@/config/implicitPrimitives';

const QUALITY_OPTIONS = [
  { value: 'draft', label: 'Draft (fast)' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'fine', label: 'Fine (slow)' },
];
const UNIT_OPTIONS = [
  { value: 'mm', label: 'Millimeters' },
  { value: 'cm', label: 'Centimeters' },
  { value: 'in', label: 'Inches' },
];

// Quality → the marching-cubes solve budget (cells). Bigger budget = a finer
// voxel is affordable = thinner walls / lighter lattices on a given part, at the
// cost of solve time. Mirrors gridBudget in implicit-microservice's lattice-fill.
const QUALITY_BUDGET: Record<string, number> = {
  draft: 17_000_000,
  balanced: 70_000_000,
  fine: 300_000_000,
};
const UNIT_SCALE: Record<string, number> = { mm: 1, cm: 10, in: 25.4 };

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(Math.max(v, lo), hi);

// The gyroid relationship (matches the server): the ImplicitGyroid thickness
// ratio = wall_mm * 10 / cell, mapped from density as ratio = 0.35 + 1.6*density,
// with a 0.4mm printable wall floor.
const densityToWall = (d: number, cell: number) =>
  Math.max(0.4, ((0.35 + d * 1.6) * cell) / 10);
const wallToDensity = (wall: number, cell: number) =>
  ((wall * 10) / cell - 0.35) / 1.6;

interface Envelope {
  cellMin: number;
  cellMax: number;
  wallMin: number;
  wallMax: number;
  densMin: number;
  densMax: number;
}

// Compute the VALID parameter band for THIS part (mm extents), quality + the
// current cell. The walls can never go below the printable floor (0.4mm) nor
// below what the solve budget can resolve on a part this big (so a huge part
// can't be asked for a sub-voxel wall that would erode or time out), nor above
// ~0.35*cell (which would be solid). Cell can't exceed ~1/3 of the part's
// thinnest dimension (need several cells across it).
function envelopeFor(
  dims: [number, number, number],
  quality: string,
  cell: number,
): Envelope {
  const [dx, dy, dz] = dims;
  const minDim = Math.min(dx, dy, dz);
  const budget = QUALITY_BUDGET[quality] ?? QUALITY_BUDGET.balanced;
  const budgetVoxel = Math.cbrt((dx * dy * dz) / budget);
  const wallFloor = Math.max(0.4, 3 * budgetVoxel);
  const cellMin = clamp(Math.max(3, wallFloor / 0.3), 3, 24);
  const cellMax = clamp(minDim / 3, cellMin + 1, 40);
  const c = clamp(cell, cellMin, cellMax);
  const wallMin = wallFloor;
  const wallMax = Math.max(wallMin + 0.1, 0.35 * c);
  const densMin = clamp(wallToDensity(wallMin, c), 0.05, 0.85);
  const densMax = clamp(wallToDensity(wallMax, c), densMin + 0.02, 0.85);
  return { cellMin, cellMax, wallMin, wallMax, densMin, densMax };
}

// Feature-tree panel for the NASA Unitcell Lattice Converter (epic #309, Plan A).
// The part is MEASURED first (lattice-analyze) so every slider gets hard limits
// computed for that specific part — the user can only land on a clean, printable
// lattice. The server clamps to the same band as a backstop.
export function LatticeConverterContent({ enabled }: { enabled: boolean }) {
  const {
    implicitPrimitiveParams,
    setImplicitPrimitiveParams,
    bumpDomainParams,
  } = useHomepage();
  const { uploading, progress, error, upload } = usePartUpload({
    pathPrefix: 'lattice-fill',
  });

  const paramsRef = useRef(implicitPrimitiveParams);
  paramsRef.current = implicitPrimitiveParams;

  const isFill = implicitPrimitiveParams.kind === 'lattice-fill';
  const meshRef = isFill ? implicitPrimitiveParams.params.meshRef : '';
  const unit = isFill ? implicitPrimitiveParams.params.unit : 'mm';

  // Measure the part (dims + B-rep edges) whenever a new one is uploaded. The
  // shared parts module owns the upload + analyze (#315 Level 2); the converter
  // just consumes the result. STL is parsed; STEP is tessellated server-side.
  const { analyzing, error: analyzeError, analysis } = usePartAnalysis(meshRef);
  const dims = analysis?.dims ?? null;

  // Once measured, seed sensible part-specific defaults (centered in the band).
  useEffect(() => {
    if (!analysis) return;
    const cur = paramsRef.current;
    if (cur.kind !== 'lattice-fill') return;
    const sdims = analysis.dims.map(
      (d) => d * (UNIT_SCALE[cur.params.unit] ?? 1),
    ) as [number, number, number];
    const minDim = Math.min(...sdims);
    const probe = envelopeFor(sdims, cur.params.quality, minDim / 5);
    const cell = clamp(minDim / 5, probe.cellMin, probe.cellMax);
    const env = envelopeFor(sdims, cur.params.quality, cell);
    const density = clamp(0.25, env.densMin, env.densMax);
    const wall = clamp(densityToWall(density, cell), env.wallMin, env.wallMax);
    setImplicitPrimitiveParams({
      kind: 'lattice-fill',
      params: {
        ...cur.params,
        cellSize: cell.toFixed(1),
        density: density.toFixed(2),
        wallThickness: wall.toFixed(2),
        previewUrl: analysis.previewUrl,
        edgesB64: analysis.edges.edgesB64,
        tangentEdgesB64: analysis.edges.tangentEdgesB64,
      },
    });
    bumpDomainParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis]);

  if (!isFill) return null;
  const p = implicitPrimitiveParams.params;
  const set = (patch: Partial<LatticeFillParams>) => {
    setImplicitPrimitiveParams({
      kind: 'lattice-fill',
      params: { ...p, ...patch },
    });
    bumpDomainParams();
  };

  const hasMesh = meshRef !== '';
  const ready = hasMesh && dims !== null && !analyzing;
  const sdims: [number, number, number] = dims
    ? [
        dims[0] * (UNIT_SCALE[unit] ?? 1),
        dims[1] * (UNIT_SCALE[unit] ?? 1),
        dims[2] * (UNIT_SCALE[unit] ?? 1),
      ]
    : [10, 10, 10];

  const cell = Number(p.cellSize) || 8;
  const env = envelopeFor(sdims, p.quality, cell);
  const wall = clamp(
    Number(p.wallThickness) || env.wallMin,
    env.wallMin,
    env.wallMax,
  );
  const density = clamp(
    Number(p.density) || env.densMin,
    env.densMin,
    env.densMax,
  );
  const lighterPct = Math.round((1 - clamp(density, 0, 1)) * 100);
  const skinOn = p.skin === 'true';
  const skinT = clamp(Number(p.skinThickness) || 1, 0.5, 5);

  // Coupled, clamped setters — density ↔ wall stay consistent; changing cell /
  // quality / unit re-clamps everything into the new valid band.
  const onCell = (c: number) => {
    const cc = clamp(c, env.cellMin, env.cellMax);
    const e = envelopeFor(sdims, p.quality, cc);
    const w = clamp(wall, e.wallMin, e.wallMax);
    const d = clamp(wallToDensity(w, cc), e.densMin, e.densMax);
    set({
      cellSize: cc.toFixed(1),
      wallThickness: w.toFixed(2),
      density: d.toFixed(2),
    });
  };
  const onDensity = (d: number) => {
    const dd = clamp(d, env.densMin, env.densMax);
    const w = clamp(densityToWall(dd, cell), env.wallMin, env.wallMax);
    set({ density: dd.toFixed(2), wallThickness: w.toFixed(2) });
  };
  const onWall = (w: number) => {
    const ww = clamp(w, env.wallMin, env.wallMax);
    const d = clamp(wallToDensity(ww, cell), env.densMin, env.densMax);
    set({ wallThickness: ww.toFixed(2), density: d.toFixed(2) });
  };
  const onQuality = (q: string) => {
    const e = envelopeFor(sdims, q, cell);
    const cc = clamp(cell, e.cellMin, e.cellMax);
    const e2 = envelopeFor(sdims, q, cc);
    const w = clamp(wall, e2.wallMin, e2.wallMax);
    const d = clamp(wallToDensity(w, cc), e2.densMin, e2.densMax);
    set({
      quality: q,
      cellSize: cc.toFixed(1),
      wallThickness: w.toFixed(2),
      density: d.toFixed(2),
    });
  };
  const onUnit = (u: string) => {
    const nd = dims
      ? (dims.map((x) => x * (UNIT_SCALE[u] ?? 1)) as [number, number, number])
      : sdims;
    const e = envelopeFor(nd, p.quality, cell);
    const cc = clamp(cell, e.cellMin, e.cellMax);
    set({ unit: u, cellSize: cc.toFixed(1) });
  };

  const handleSelect = async (file: File) => {
    const res = await upload(file);
    if (res) set({ meshRef: res.meshRef, meshName: res.meshName });
  };

  const sliderRow = (
    label: string,
    valueText: string,
    helper: string | null,
    sl: { min: number; max: number; step: number; value: number; def: number },
    onChange: (v: number) => void,
  ) => (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-telekinesis-neutral-50">{label}</span>
        <span className="text-xs text-[#107d40]">{valueText}</span>
      </div>
      <Slider
        disabled={!enabled}
        min={sl.min}
        max={sl.max}
        step={sl.step}
        value={[sl.value]}
        defaultValue={[sl.def]}
        defaultMarkerStyle="line"
        onValueChange={([v]) => onChange(v)}
      />
      {helper && (
        <p className="text-[10px] leading-snug text-telekinesis-neutral-400">
          {helper}
        </p>
      )}
    </div>
  );

  return (
    <div>
      <p className="mb-2 text-xs font-semibold text-telekinesis-neutral-50">
        {IMPLICIT_PRIMITIVE_LABELS['lattice-fill']}
      </p>
      <div className="space-y-3">
        {/* Upload part — the shared parts module's upload control + real
            byte-progress bar (#315 Level 2). Everything below is gated on
            measuring the part. */}
        <PartUploadField
          enabled={enabled}
          uploading={uploading}
          progress={progress}
          error={error}
          meshName={p.meshName}
          onSelect={handleSelect}
          onRemove={() => set({ meshRef: '', meshName: '', previewUrl: '' })}
          hint="Upload a solid part to fill it with a lighter lattice."
        />

        {/* Computing lattice parameters — the part is being measured so the
            sliders below can be limited to clean, printable values for it. */}
        {hasMesh && analyzing && (
          <div className="space-y-1 pt-1">
            <p className="text-[11px] text-telekinesis-neutral-50">
              Computing Lattice Parameters…
            </p>
            {/* Indeterminate: the server-side measure has no progress signal,
                so a segment sweeps across (honest "working"), not a static
                fixed-width pulse that reads as stuck at 33%. */}
            <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-telekinesis-neutral-800">
              <div className="absolute inset-y-0 left-0 w-1/4 animate-indeterminate-bar rounded-full bg-[#107d40]" />
            </div>
            <p className="text-[10px] leading-snug text-telekinesis-neutral-400">
              Measuring the part to set valid limits.
            </p>
          </div>
        )}
        {hasMesh && !analyzing && analyzeError && (
          <p className="text-[10px] leading-snug text-red-400">
            {analyzeError} — try re-uploading the part.
          </p>
        )}

        {/* Part-specific parameters — only after the part is measured. */}
        {ready && (
          <>
            <p className="text-[10px] text-telekinesis-neutral-400">
              Part: {sdims[0].toFixed(0)} × {sdims[1].toFixed(0)} ×{' '}
              {sdims[2].toFixed(0)} {unit}
            </p>

            {/* Lattice style */}
            <div className="space-y-1">
              <span className="text-xs text-telekinesis-neutral-50">
                Lattice Style
              </span>
              <Select
                disabled={!enabled}
                value={p.topology}
                onValueChange={(v) =>
                  set({ topology: v as LatticeFillParams['topology'] })
                }
              >
                <SelectTrigger className="h-8 w-full rounded-md border border-telekinesis-neutral-700 bg-telekinesis-neutral-800 px-2.5 text-xs text-telekinesis-neutral-50 hover:bg-telekinesis-neutral-700 focus:ring-0 focus:ring-offset-0">
                  <SelectValue placeholder="Lattice Style" />
                </SelectTrigger>
                <SelectContent className="border-telekinesis-neutral-700 bg-telekinesis-neutral-800 text-telekinesis-neutral-50">
                  <SelectGroup>
                    <SelectLabel className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-telekinesis-neutral-400">
                      Lattice Style
                    </SelectLabel>
                    {NASA_LATTICE_TOPOLOGY_ORDER.map((t) => (
                      <SelectItem
                        key={t}
                        value={t}
                        className="text-xs focus:bg-[#107d40]/20 focus:text-telekinesis-neutral-50 data-[state=checked]:bg-telekinesis-neutral-700"
                      >
                        {IMPLICIT_PRIMITIVE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {sliderRow(
              'Lattice Density',
              density.toFixed(2),
              `≈ ${lighterPct}% lighter than solid in the lattice region.`,
              {
                min: +env.densMin.toFixed(2),
                max: +env.densMax.toFixed(2),
                step: 0.01,
                value: density,
                def: clamp(0.25, env.densMin, env.densMax),
              },
              onDensity,
            )}

            {sliderRow(
              'Cell Size',
              `${cell.toFixed(1)} mm`,
              null,
              {
                min: +env.cellMin.toFixed(1),
                max: +env.cellMax.toFixed(1),
                step: 0.5,
                value: cell,
                def: cell,
              },
              onCell,
            )}

            {sliderRow(
              'Wall Thickness',
              `${wall.toFixed(2)} mm`,
              null,
              {
                min: +env.wallMin.toFixed(2),
                max: +env.wallMax.toFixed(2),
                step: 0.05,
                value: wall,
                def: wall,
              },
              onWall,
            )}

            {/* Solid skin toggle + thickness */}
            <div className="flex items-center gap-2 pt-1">
              <label className="flex-1 text-xs text-telekinesis-neutral-50">
                Solid Skin
              </label>
              <input
                type="checkbox"
                disabled={!enabled}
                checked={skinOn}
                onChange={(e) =>
                  set({ skin: e.target.checked ? 'true' : 'false' })
                }
                className="h-4 w-4"
              />
              <span className="w-12 text-right text-xs text-telekinesis-neutral-50">
                {skinOn ? 'on' : 'off'}
              </span>
            </div>
            {skinOn &&
              sliderRow(
                'Skin Thickness',
                `${skinT.toFixed(2)} mm`,
                null,
                { min: 0.5, max: 5, step: 0.1, value: skinT, def: 1 },
                (s) => set({ skinThickness: clamp(s, 0.5, 5).toFixed(2) }),
              )}

            {/* Quality (solve budget) */}
            <div className="space-y-1">
              <span className="text-xs text-telekinesis-neutral-50">
                Quality
              </span>
              <Select
                disabled={!enabled}
                value={p.quality}
                onValueChange={onQuality}
              >
                <SelectTrigger className="h-8 w-full rounded-md border border-telekinesis-neutral-700 bg-telekinesis-neutral-800 px-2.5 text-xs text-telekinesis-neutral-50 hover:bg-telekinesis-neutral-700 focus:ring-0 focus:ring-offset-0">
                  <SelectValue placeholder="Quality" />
                </SelectTrigger>
                <SelectContent className="border-telekinesis-neutral-700 bg-telekinesis-neutral-800 text-telekinesis-neutral-50">
                  <SelectGroup>
                    {QUALITY_OPTIONS.map((o) => (
                      <SelectItem
                        key={o.value}
                        value={o.value}
                        className="text-xs focus:bg-[#107d40]/20 focus:text-telekinesis-neutral-50 data-[state=checked]:bg-telekinesis-neutral-700"
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            {/* Part units */}
            <div className="space-y-1">
              <span className="text-xs text-telekinesis-neutral-50">
                Part Units
              </span>
              <Select disabled={!enabled} value={p.unit} onValueChange={onUnit}>
                <SelectTrigger className="h-8 w-full rounded-md border border-telekinesis-neutral-700 bg-telekinesis-neutral-800 px-2.5 text-xs text-telekinesis-neutral-50 hover:bg-telekinesis-neutral-700 focus:ring-0 focus:ring-offset-0">
                  <SelectValue placeholder="Units" />
                </SelectTrigger>
                <SelectContent className="border-telekinesis-neutral-700 bg-telekinesis-neutral-800 text-telekinesis-neutral-50">
                  <SelectGroup>
                    {UNIT_OPTIONS.map((o) => (
                      <SelectItem
                        key={o.value}
                        value={o.value}
                        className="text-xs focus:bg-[#107d40]/20 focus:text-telekinesis-neutral-50 data-[state=checked]:bg-telekinesis-neutral-700"
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
